import { useQuery } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { MRT_SortingState } from "material-react-table";
import { mrtSortingStateToObject } from "src/api/apiHelpers";
import { z } from "zod";
import { zodParseWithDetailedLog } from "src/api/apiErrorLogs";

export const getAllDataListValueOverride = "getAllDataListValueOverride" as const;

type Options = {
    page?: number;
    itemsPerPage?: number;
    search?: string;
    sorting?: MRT_SortingState;
    dataListVersionIds?: number[];
};

type ParamsType = {
    params: {
        page: number;
        itemsPerPage: number;
        q: string;
        order?: Record<string, string>;
        dataListVersionId?: number[];
    };
};

export const useGetAllDataListValueOverride = (options?: Options) => {
    const { page = 1, itemsPerPage = 200, search = "", sorting = [], dataListVersionIds } = options || {};

    const params: ParamsType = {
        params: {
            page,
            itemsPerPage,
            q: search,
            order: mrtSortingStateToObject(sorting),
            dataListVersionId: dataListVersionIds,
        },
    };

    const query = useQuery({
        queryKey: [getAllDataListValueOverride, page, itemsPerPage, search, sorting, dataListVersionIds],
        queryFn: async () => {
            return await loggedInApiCall.get("/api/data_list_version_value_overrides", params);
        },
        select: (request) => {
            const totalItems = request.data["hydra:totalItems"];
            const overrides = request.data["hydra:member"];

            const data = zodParseWithDetailedLog(overrides, DataListValueOverrideSchema.array(), {
                operation: "GET",
                endpoint: "/data_list_version_value_overrides",
            });

            return {
                items: data,
                totalItems: totalItems,
            };
        },
    });

    return {
        overrides: query.data?.items,
        totalItems: query.data?.totalItems,
        ...query,
    };
};

export const DataListOptionValues = z.object({
    type: z.string(),
    context: z.record(z.string(), z.string().or(z.number())),
});

export const DataListOptionValueOverrideSchema = DataListOptionValues.extend({
    id: z.number(),
    "@id": z.string(),
    dataListOptionCode: z.string().optional(),
});

export const DataListExtraOptionSchema = DataListOptionValues.extend({
    id: z.number(),
    "@id": z.string(),
    code: z.string(),
    label: z.string(),
});

export const DataListValueOverrideSchema = z.object({
    id: z.number(),
    dataListVersionId: z.number(),
    dataListOptionValueOverrides: z.array(DataListOptionValueOverrideSchema),
    dataListExtraOptions: z.array(DataListExtraOptionSchema),
    canView: z.boolean().optional(),
    canModify: z.boolean().optional(),
    canDelete: z.boolean().optional(),
});

export type DataListOptionValueOverride = z.infer<typeof DataListOptionValueOverrideSchema>;
export type DataListExtraOption = z.infer<typeof DataListExtraOptionSchema>;
export type DataListValueOverride = z.infer<typeof DataListValueOverrideSchema>;
